<template>
  <div class="days-grid">
    <div class="p-d-flex p-flex-wrap p-ai-center p-mt-2">
      <div class="p-p-1">
        <Button class="p-button-text"
                icon="ti-files"
                :label="$t('Copy Mon–Fri')"
                @click="copyToWeekdays"/>
      </div>
      <div class="p-p-1">
        <Button class="p-button-text"
                icon="ti-layers"
                :label="$t('Copy to All Days')"
                @click="copyToAllDays"/>
      </div>
    </div>

    <div v-for="d in dayKeys" :key="d" class="day-row theme-border-color">
      <div class="day-col">
        <div class="p-d-flex p-ai-center">
          <InputSwitch :id="`day-${d}`" v-model="dayEnabled[d]" />
          <label :for="`day-${d}`" class="day-label pointer">{{ dayLabel(d) }}</label>
        </div>
        <small class="muted" v-if="!dayEnabled[d]">{{ $t('Closed') }}</small>
      </div>

      <div class="intervals-col">
        <div v-if="dayEnabled[d]" class="intervals-wrap">
          <div v-for="(it, idx) in dayIntervals[d]" :key="`${d}-${idx}`" class="interval-row">
            <InputMask class="time-input"
                       :class="{ 'p-invalid': submitted && hasIntervalError(d, idx) }"
                       mask="99:99"
                       placeholder="HH:MM"
                       v-model="it.from" />

            <span class="dash">—</span>

            <InputMask class="time-input"
                       :class="{ 'p-invalid': submitted && hasIntervalError(d, idx) }"
                       mask="99:99"
                       placeholder="HH:MM"
                       v-model="it.to" />

            <Button class="p-button-text p-button-danger p-button-sm"
                    icon="ti-close"
                    @click="removeInterval(d, idx)" />
          </div>

          <div class="interval-actions">
            <Button class="p-button-text"
                    icon="ti-plus"
                    :label="$t('Add Interval')"
                    @click="addInterval(d)"/>

            <Button class="p-button-text"
                    icon="ti-clipboard"
                    :label="$t('Copy Day')"
                    @click="copyDay(d)" />
          </div>

          <small class="p-invalid" v-if="submitted && dayErrorKey[d]">
            {{ $t(dayErrorKey[d]) }}
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HoursEditor',
  props: {
    modelValue: { type: Object, default: null }, // draft hours
    submitted: { type: Boolean, default: false },
    dayKeys: { type: Array, default: () => ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] },
  },
  emits: ['update:modelValue', 'valid'],
  data() {
    const blankEnabled = {};
    const blankIntervals = {};
    const blankErr = {};
    for (const d of ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']) {
      blankEnabled[d] = false;
      blankIntervals[d] = [];
      blankErr[d] = '';
    }

    return {
      dayEnabled: { ...blankEnabled },
      dayIntervals: { ...blankIntervals },
      dayErrorKey: { ...blankErr },

      validateTimer: null,
      syncingFromParent: false,
      lastEmittedJson: null,
    };
  },
  mounted() {
    this.applyFromModelValue();
  },
  watch: {
    modelValue: {
      deep: true,
      handler() { this.applyFromModelValue(); }
    },
    dayEnabled: {
      deep: true,
      handler() { this.onLocalChanged(); }
    },
    dayIntervals: {
      deep: true,
      handler() { this.onLocalChanged(); }
    },
    submitted(val) {
      if (val) this.revalidateDebounced();
    }
  },
  methods: {
    validate() {
      const ok = this.validateAll();
      this.$emit('valid', ok);
      return ok;
    },
    getPayload() {
      return this.buildHoursValidPayload();
    },

    dayLabel(k) {
      const map = {
        Mo: this.$t('Monday'),
        Tu: this.$t('Tuesday'),
        We: this.$t('Wednesday'),
        Th: this.$t('Thursday'),
        Fr: this.$t('Friday'),
        Sa: this.$t('Saturday'),
        Su: this.$t('Sunday'),
      };
      return map[k] || k;
    },

    normalizeDraft(v) {
      const src = (v && typeof v === 'object') ? v : {};
      const out = {};
      for (const d of this.dayKeys) {
        const arr = Array.isArray(src[d]) ? src[d] : [];
        out[d] = arr.map(x => [
          (x && x[0] != null) ? String(x[0]) : '',
          (x && x[1] != null) ? String(x[1]) : '',
        ]);
      }
      return out;
    },
    stableJsonDraft(v) {
      return JSON.stringify(this.normalizeDraft(v));
    },

    applyFromModelValue() {
      const source = (this.modelValue && typeof this.modelValue === 'object') ? this.modelValue : {}

      const incomingJson = this.stableJsonDraft(source)
      if (incomingJson === this.lastEmittedJson) return

      this.syncingFromParent = true

      const norm = this.normalizeDraft(source)

      for (const d of this.dayKeys) {
        const arr = Array.isArray(norm[d]) ? norm[d] : []

        this.dayEnabled[d] = arr.length > 0
        this.dayIntervals[d] = arr.map(x => ({ from: x?.[0] ?? '', to: x?.[1] ?? '' }))
      }

      this.$nextTick(() => {
        this.syncingFromParent = false
        this.lastEmittedJson = incomingJson
        if (this.submitted) this.validateAll()
      })
    },

    onLocalChanged() {
      if (this.syncingFromParent) return;

      // ✅ emit DRAFT without filtering (so day won't "close" while typing)
      const draft = this.buildHoursDraftPayload();
      const json = this.stableJsonDraft(draft);

      if (json === this.lastEmittedJson) return;

      this.lastEmittedJson = json;
      this.$emit('update:modelValue', draft);

      if (this.submitted) this.revalidateDebounced();
    },

    revalidateDebounced() {
      clearTimeout(this.validateTimer);
      this.validateTimer = setTimeout(() => this.validateAll(), 120);
    },

    // ---- actions ----
    addInterval(day) {
      if (!this.dayEnabled[day]) this.dayEnabled[day] = true;

      const items = this.dayIntervals[day] || [];
      const last = items[items.length - 1];

      const from = (last && this.isValidTime(last.to)) ? last.to : '09:00';
      const toMin = this.isValidTime(from) ? this.toMinutes(from) + 60 : 18 * 60;
      const to = `${String(Math.floor(toMin / 60)).padStart(2, '0')}:${String(toMin % 60).padStart(2, '0')}`;

      this.dayIntervals[day].push({ from, to });
    },
    removeInterval(day, idx) {
      this.dayIntervals[day].splice(idx, 1);
      if (this.dayIntervals[day].length === 0) this.dayEnabled[day] = false;
    },

    copyDay(fromDay) {
      const src = (this.dayIntervals[fromDay] || []).map(x => ({ ...x }));
      for (const d of this.dayKeys) {
        if (d === fromDay) continue;
        this.dayEnabled[d] = src.length > 0;
        this.dayIntervals[d] = src.map(x => ({ ...x }));
      }
    },
    copyToWeekdays() {
      const src = (this.dayIntervals.Mo || []).map(x => ({ ...x }));
      for (const d of ['Mo', 'Tu', 'We', 'Th', 'Fr']) {
        this.dayEnabled[d] = src.length > 0;
        this.dayIntervals[d] = src.map(x => ({ ...x }));
      }
    },
    copyToAllDays() {
      const src = (this.dayIntervals.Mo || []).map(x => ({ ...x }));
      for (const d of this.dayKeys) {
        this.dayEnabled[d] = src.length > 0;
        this.dayIntervals[d] = src.map(x => ({ ...x }));
      }
    },

    // ---- validation ----
    isValidTime(s) {
      if (!s || typeof s !== 'string') return false;
      return /^([01]\d|2[0-3]):([0-5]\d)$/.test(s);
    },
    toMinutes(s) {
      const [h, m] = s.split(':').map(Number);
      return h * 60 + m;
    },
    hasIntervalError(day, idx) {
      const it = this.dayIntervals[day]?.[idx];
      if (!it) return false;
      if (!this.isValidTime(it.from) || !this.isValidTime(it.to)) return true;
      return this.toMinutes(it.from) >= this.toMinutes(it.to);
    },

    validateAll() {
      // reset
      const err = {};
      for (const d of this.dayKeys) err[d] = '';
      this.dayErrorKey = err;

      for (const d of this.dayKeys) {
        if (!this.dayEnabled[d]) continue;

        const items = this.dayIntervals[d] || [];
        if (!items.length) {
          this.dayErrorKey[d] = 'addAtLeastOneIntervalErr'; // "Add at least one interval or disable the day"
          continue;
        }

        const slots = [];
        for (let i = 0; i < items.length; i++) {
          if (this.hasIntervalError(d, i)) {
            this.dayErrorKey[d] = 'Invalid time interval';
            break;
          }
          slots.push([this.toMinutes(items[i].from), this.toMinutes(items[i].to)]);
        }
        if (this.dayErrorKey[d]) continue;

        slots.sort((a, b) => a[0] - b[0]);
        for (let i = 1; i < slots.length; i++) {
          if (slots[i][0] < slots[i - 1][1]) {
            this.dayErrorKey[d] = 'Intervals overlap';
            break;
          }
        }
      }

      const ok = !Object.values(this.dayErrorKey).some(Boolean);
      this.$emit('valid', ok);
      return ok;
    },

    // ✅ DRAFT payload: no filtering (preserve empty inputs while typing)
    buildHoursDraftPayload() {
      const out = {};
      for (const d of this.dayKeys) {
        if (!this.dayEnabled[d]) {
          out[d] = [];
          continue;
        }
        out[d] = (this.dayIntervals[d] || []).map(x => [
          (x?.from ?? ''),
          (x?.to ?? ''),
        ]);
      }
      return out;
    },

    // ✅ VALID payload: filter & map for backend (use via ref.getPayload())
    buildHoursValidPayload() {
      const out = {};
      for (const d of this.dayKeys) {
        if (!this.dayEnabled[d]) {
          out[d] = [];
          continue;
        }
        out[d] = (this.dayIntervals[d] || [])
            .filter(x => this.isValidTime(x.from) && this.isValidTime(x.to) && this.toMinutes(x.from) < this.toMinutes(x.to))
            .map(x => [x.from, x.to]);
      }
      return out;
    },
  }
};
</script>

<style scoped lang="scss">
.days-grid { display:flex; flex-direction:column; gap:12px; }
.day-row { display:grid; grid-template-columns:220px 1fr; gap:16px; align-items:start; padding:12px 0; border-top:1px solid; }
.day-row:first-child { border-top:0; }
.day-col { padding-top:4px; }
.day-label { margin-left:10px; font-weight:600; }
.muted { display:block; margin-left:52px; margin-top:4px; }
.intervals-wrap { width:100%; }
.interval-row { display:flex; align-items:center; gap:10px; margin-bottom:10px; }
.time-input { width:110px; }
.dash { opacity:.7; }
.interval-actions { display:flex; gap:10px; margin-top:2px; flex-wrap:wrap; }

@media (max-width: 640px) {
  .day-row { grid-template-columns:1fr; gap:10px; padding:14px 0; }
  .day-col { padding-top:0; display:flex; align-items:center; justify-content:space-between; }
  .day-label { margin-left:12px; }
  .muted { margin-left:0; margin-top:6px; }
  .interval-row { display:grid; grid-template-columns:1fr 1fr auto; gap:10px; align-items:center; }
  .time-input { width:100%; }
  .dash { display:none; }
  .interval-actions { justify-content:flex-start; }
}
</style>

